import { useId, useRef, useState } from "react";
import {
  DisplaySettings,
  FavSettings,
  NgSettings,
  OtherSettings,
} from "@/features/settings/components/sections";
import {
  SETTINGS_TABS,
  type SettingsTabDefinition,
  type SettingsTabId,
} from "@/features/settings/utils/settingsTabs";
import { useCurrentUrlSlug } from "@/shared/hooks/useCurrentUrlSlug";
import { cn } from "@/shared/lib";
import { getNextTabIndex } from "@/shared/lib/tabListKeyboardNavigation";
import { Modal } from "@/shared/ui/overlay";

interface DesktopSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TABS: readonly SettingsTabDefinition[] = SETTINGS_TABS;

/**
 * PC版の設定モーダル。SP版の`SettingsPage`が使う既存の設定セクション
 * (DisplaySettings/NgSettings/FavSettings/OtherSettings)をタブ切替で
 * そのまま内包する。設定の状態管理(各セクション内のZustandストア)は
 * SP版と共用し、外枠のみモーダル用に作り直す(#34)。
 *
 * タブ切替はWAI-ARIA APGのTab pattern(role="tablist"/"tab"/"tabpanel"、
 * roving tabindex)に沿って実装する。
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
export const DesktopSettingsModal: React.FunctionComponent<
  DesktopSettingsModalProps
> = ({ isOpen, onClose }: DesktopSettingsModalProps) => {
  const [activeTab, setActiveTab] = useState<SettingsTabId>("display");
  const currentUrlSlug = useCurrentUrlSlug();
  const baseId = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // SP版のSettingsPageは開くたびに新規マウントされ常に"display"タブから
  // 始まる。PC版のモーダルはCatalogPageに永続マウントされ、isOpenの
  // 切替だけで表示/非表示するためactiveTabが前回値のまま残る。開くたびに
  // "display"へリセットし、SP版と挙動を揃える。レンダー中に状態を調整する
  // Reactの推奨パターンを使う(https://react.dev/learn/you-might-not-need-an-effect)
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen) setActiveTab("display");
  }

  const activeIndex = TABS.findIndex((tab) => tab.id === activeTab);

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ): void => {
    const nextIndex = getNextTabIndex(activeIndex, event.key, TABS.length);
    if (nextIndex === null) return;
    const nextTab = TABS[nextIndex];
    if (!nextTab) return;
    event.preventDefault();
    setActiveTab(nextTab.id);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      position="top"
      title="設定"
      width="settings"
    >
      <div
        role="tablist"
        aria-label="設定カテゴリ"
        className="sticky top-0 z-10 flex min-w-0 overflow-x-auto border-b border-border bg-card"
      >
        {TABS.map((tab, index) => {
          const selected = activeTab === tab.id;
          return (
            <button
              type="button"
              key={tab.id}
              ref={(element: HTMLButtonElement | null): void => {
                tabRefs.current[index] = element;
              }}
              id={`${baseId}-tab-${tab.id}`}
              role="tab"
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={cn(
                "flex-1 shrink-0 whitespace-nowrap px-4 py-3 text-sm font-medium transition-colors",
                selected
                  ? "text-primary border-b-2 border-primary"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={(): void => setActiveTab(tab.id)}
              onKeyDown={handleTabKeyDown}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        id={`${baseId}-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`${baseId}-tab-${activeTab}`}
        className="p-4 space-y-6"
      >
        {(activeTab === "display" ||
          activeTab === "catalog" ||
          activeTab === "thread") && (
          <DisplaySettings
            category={activeTab}
            currentUrlSlug={currentUrlSlug}
          />
        )}
        {activeTab === "ng" && <NgSettings />}
        {activeTab === "fav" && <FavSettings />}
        {activeTab === "other" && <OtherSettings />}
      </div>
    </Modal>
  );
};
